import React from "react";

// @mui material components
import Checkbox from "@mui/material/Checkbox";

// Material Dashboard 2 PRO React TS components
import MDBox from "@cloudpremise/portal_core/src/themes/mui/components/MDBox";
import MDTypography from "@cloudpremise/portal_core/src/themes/mui/components/MDTypography";
import MDInput from "@cloudpremise/portal_core/src/themes/mui/components/MDInput";
import MDButton from "@cloudpremise/portal_core/src/themes/mui/components/MDButton";
import Icon from "@mui/material/Icon";
import MDSelect from "@cloudpremise/portal_core/src/themes/mui/components/MDSelect";
import ExportIcon from "@mui/icons-material/IosShare";
import { Shipment } from "../../types/Shipment";
import { useDataProvider } from '@cloudpremise/portal_core';
import CircularProgress from '@mui/material/CircularProgress';

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Declaring props types for IdCell
interface Props {
    UnitNo: string;
    WaybillDest: string;
    Origin: string;
    CurrentEvent: string;
    onSearch: any;
    onExport: any;
}

function SearchForm({UnitNo, WaybillDest, Origin, CurrentEvent, onSearch}: Props): JSX.Element {
    const defaultState = {
        UnitNo: UnitNo,
        WaybillDest: WaybillDest,
        Origin: Origin,
        CurrentEvent: CurrentEvent,
    };

    const [state, setState] = React.useReducer((state: any, newState: Partial<any>) => ({
        ...state,
        ...newState
    }), defaultState);

    const shipment: Shipment = {
        sObjectTypeName: "Shipment",
        url: "/v1/sobject",
        params: {
            search: state,
        }
    }

    const [loading, data, adapterState, setAdapterState] = useDataProvider(shipment, {
        "RF_LIMIT": 1000,
        "RF_OFFSET": 0,
        "callApi": false,
        "provider": "firebase"
    }, false, (data) => {
        let rows = [];
        if(Array.isArray(data.result)){
            rows = data.result;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');
        if(rows.length > 0){
            const headers = Object.keys(rows[0]);
            worksheet.addRow(headers);

            rows.forEach(row => {
                // Map the row values to the correct order based on headers
                const rowData = headers.map(header => row[header]);
                worksheet.addRow(rowData);
            });
        }

        // Buffer to store the generated Excel file
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
            saveAs(blob, "Portal-export.xlsx");
        });
    });

    const originOptions = [
        "ROFF,OK",
        "MACON,MT",
        "JASPER,MN",
        "BRUNSWICK,NE",
        "CHAMBERLA,SD",
        "WEDRON,IL",
        "OREGON,IL",
        "WATERTOWN,SD",
        "LAUREL,MT",
        "WCHANDLER,AZ",
        "GREFALLS,MT",
        "LOGPARCHI,IL",
        "WARDEN,WA"
    ];

    function handleChange(event, name){
        setState({
            [name]: event.currentTarget.value
        });
    }
    function onSelectChange(newValue, name){
        setState({
            [name]: newValue
        });
    }

    function hasSearchData(){
        let hasSearchData = false;
        for(let key in state){
            const value = state[key];
            if(value && value.length > 0){
                hasSearchData = true;
                break;
            }
        }
        return hasSearchData;
    }

    function onExport(){
        setAdapterState({
            loading: false,
            callApi: true,
        });
    }

    return (
        <MDBox display="flex"
        sx={({ breakpoints }) => ({
            flex: "1 0 0",
            flexWrap: "wrap",
            gap: "5px",
            justifyContent: "flex-end",
            [breakpoints.down("md")]: {
                justifyContent: "flex-start",
                marginTop: "8px"
            }
        })}>
            <MDBox>
                <MDInput
                    label="Unit"
                    value={state.UnitNo}
                    fullWidth
                    onChange={(event: any) => handleChange(event, "UnitNo")}
                />
            </MDBox>
            <MDBox>
                <MDSelect
                    defaultValue=""
                    options={originOptions}
                    label="Origin"
                    onChange={(event: any, newValue) => onSelectChange(newValue, 'Origin')}
                    boxProps={{minWidth: "200px"}}
                />
            </MDBox>
            <MDBox>
                <MDInput
                    label="Waybill Dest"
                    value={state.WaybillDest}
                    fullWidth
                    onChange={(event: any) => handleChange(event, "WaybillDest")}
                />
            </MDBox>
            <MDBox>
                <MDButton variant="gradient" color="info" onClick={() => onSearch(state)}>
                    <Icon>search</Icon>
                    &nbsp;Search
                </MDButton>
            </MDBox>
            <MDBox>
                <MDButton variant="outlined" disabled={((hasSearchData() === false) && (loading === false))} color="info" onClick={onExport}>
                    {
                        loading ?
                            <CircularProgress color="info" size={11} />
                        :
                            <>
                                <ExportIcon />
                            </>
                    }
                    &nbsp;Export
                </MDButton>
            </MDBox>
        </MDBox>
    );
}

// Declaring default props for SearchForm
SearchForm.defaultProps = {
    UnitNo: '',
    WaybillDest: '',
    Origin: '',
    CurrentEvent: '',
    onSearch: function(){ },
    onExport: function(){ }
};

export default SearchForm;
